import { useMemo, useState } from 'react';
import { hrApi } from '@/api';
import { EmployeeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import type { HrFeedback360Summary } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrFeedback360Page() {
  const [cycleId, setCycleId] = useState('');  const [revieweeId, setRevieweeId] = useState('');
  const [summary, setSummary] = useState<HrFeedback360Summary | null>(null);

  const launch = async () => {
    try {
      await hrApi.launchFeedback360(Number(cycleId), Number(revieweeId));
      notify.success('360 feedback invitations sent');
    } catch (err) {
      showApiError(err, 'Launch failed');
    }
  };

  const loadSummary = async () => {
    try {
      setSummary(await hrApi.feedbackSummary(Number(cycleId), Number(revieweeId)));
    } catch (err) {
      showApiError(err, 'Failed to load summary');
    }
  };

  const feedbackRows = useMemo(() => {
    if (!summary?.byRelationship) return [];
    return Object.entries(summary.byRelationship).flatMap(([relationship, responses]) =>
      responses.map((response) => ({
        invitationId: response.invitationId,
        relationship,
        relationshipType: response.relationshipType,
        rating: response.rating,
        statusCode: response.statusCode,
        comments: response.comments,
      })),
    );
  }, [summary]);

  const feedbackColumns: Column<(typeof feedbackRows)[number]>[] = [
    { key: 'relationship', header: 'Relationship' },
    { key: 'relationshipType', header: 'Type' },
    { key: 'rating', header: 'Rating' },
    { key: 'statusCode', header: 'Status' },
    { key: 'comments', header: 'Comments' },
  ];

  return (
    <div>
      <PageHeader
        title="360° Feedback"
        description="Peer, manager, and subordinate feedback cycles"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: '360 Feedback' }]}
      />
      <div className="card mb-4 flex flex-wrap gap-3 p-4">
        <Input placeholder="Cycle ID" value={cycleId} onChange={(e) => setCycleId(e.target.value)} />
        <EmployeeSelect label="Reviewee" value={revieweeId} onChange={setRevieweeId} showDetails={false} hrScoped />
        <Button onClick={launch}>Launch 360</Button>
        <Button variant="secondary" onClick={loadSummary}>View summary</Button>
      </div>
      {summary && (
        <div className="card p-4">
          <p className="text-sm">Average rating: {summary.averageRating ?? '—'}</p>
          <p className="text-sm">
            Responses: {summary.completedResponses} / {summary.totalInvitations}
          </p>
          <DataTable
            columns={feedbackColumns}
            data={feedbackRows}
            keyField="invitationId"
            embedded
            emptyTitle="No feedback responses yet"
          />
        </div>
      )}
    </div>
  );
}
